import React, { Suspense, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import clsx from 'clsx';
import { useAdmin } from '../../../hooks/useAdmin';
import { useNavViewer } from '../../../hooks/useNavViewer';
import { PERMISSIONS, type Permission } from '../../../types/permissions';
import { UtLoading, UT_LOGO } from '../primitives';

interface AdminNavItem {
  label: string;
  to: string;
  /** Extra paths that count as this item being active. */
  also?: string[];
  /** Visible when the admin has any of these permissions; `super` means super admins only. */
  perm?: Permission[] | 'super';
  /** One line for the admin home tile. */
  desc: string;
}

/**
 * Admin navigation. The design's three groups, extended with the admin pages
 * the design doesn't cover so nothing becomes unreachable. Visibility follows
 * the same permission checks as the AdminPortal cards.
 */
export const ADMIN_NAV: { group: string; items: AdminNavItem[] }[] = [
  {
    group: 'Match',
    items: [
      { label: 'Games', to: '/admin/games', desc: 'Create and edit games, venues and presets', perm: [PERMISSIONS.MANAGE_GAMES] },
      { label: 'Complete games', to: '/admin/history', desc: 'Record scores, payments and results', perm: [PERMISSIONS.MANAGE_HISTORY] },
      { label: 'Team balancing', to: '/admin/team-balancing', desc: 'Balance, swap and save the teams', also: ['/admin/teams/'], perm: [PERMISSIONS.MANAGE_TEAMS] },
      { label: 'Slot offers', to: '/admin/slot-offers', desc: 'Reserve queue after drop-outs', perm: [PERMISSIONS.MANAGE_SLOTS] },
      { label: 'Payments', to: '/admin/payments', desc: 'Who has paid for each game', perm: [PERMISSIONS.MANAGE_PAYMENTS] },
    ],
  },
  {
    group: 'Players',
    items: [
      { label: 'Players', to: '/admin/players', desc: 'Search, edit, merge and import players', perm: [PERMISSIONS.MANAGE_PLAYERS] },
      { label: 'Photos', to: '/admin/photos', desc: 'Keep or remove uploaded player photos', perm: [PERMISSIONS.MANAGE_PLAYERS] },
      { label: 'Tokens', to: '/admin/tokens', desc: 'Priority, shield and injury tokens', also: ['/admin/shields', '/admin/injuries'], perm: [PERMISSIONS.MANAGE_TOKENS, PERMISSIONS.MANAGE_PLAYERS] },
      { label: 'Ratings', to: '/admin/ratings', desc: 'Peer ratings and rater activity', perm: [PERMISSIONS.MANAGE_RATINGS] },
    ],
  },
  {
    group: 'System',
    items: [
      { label: 'Flags & roles', to: '/admin/feature-flags', desc: 'Feature flags, rollout and roles', also: ['/admin/roles'], perm: 'super' },
      { label: 'Admins', to: '/admin/admins', desc: 'Admin accounts and permissions', also: ['/admin/permissions'], perm: [PERMISSIONS.MANAGE_ADMINS] },
      { label: 'Accounts', to: '/admin/account-management', desc: 'Password resets and beta testers', perm: [PERMISSIONS.MANAGE_ACCOUNTS] },
      { label: 'WhatsApp bot', to: '/admin/whatsapp-bot', desc: 'Kill switch, outbox and interactions', perm: [PERMISSIONS.MANAGE_GAMES] },
      { label: 'Diagnostics', to: '/admin/session-diagnostics', desc: 'Session and login diagnostics', perm: [PERMISSIONS.MANAGE_ACCOUNTS] },
      { label: 'XP comparison', to: '/admin/xp-comparison', desc: 'Compare XP v1 and v2', perm: 'super' },
    ],
  },
];

export function useVisibleAdminNav() {
  const { isSuperAdmin, hasPermission } = useAdmin();
  return ADMIN_NAV.map((g) => ({
    group: g.group,
    items: g.items.filter((i) => {
      if (!i.perm) return true;
      if (i.perm === 'super') return isSuperAdmin;
      return isSuperAdmin || i.perm.some((p) => hasPermission(p));
    }),
  })).filter((g) => g.items.length > 0);
}

const isActive = (item: AdminNavItem, pathname: string) =>
  pathname === item.to || pathname.startsWith(`${item.to}/`) || (item.also ?? []).some((a) => pathname.startsWith(a));

/**
 * Ultimate Team admin shell: "WNF ADMIN" header with role and EXIT ADMIN, a
 * 210px sidebar from 834px, a scrolling chip row below it. Admin pages that
 * aren't rebuilt yet render inside `.ut-classic`, which maps their DaisyUI
 * colours to UT tokens.
 */
export const UtAdminShell: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { pathname } = useLocation();
  const { isSuperAdmin, isAdmin } = useAdmin();
  const viewer = useNavViewer();
  const nav = useVisibleAdminNav();
  const flat = nav.flatMap((g) => g.items);
  const chipRow = useRef<HTMLDivElement>(null);

  // Keep the active chip in view on mobile.
  useEffect(() => {
    chipRow.current?.querySelector('[aria-current="page"]')?.scrollIntoView({ inline: 'center', block: 'nearest' });
  }, [pathname]);

  const role = isSuperAdmin ? 'Super admin' : isAdmin ? 'Admin' : 'Viewer';

  return (
    <div className="ut-root flex min-h-screen flex-col bg-ut-bg2 font-ut-body text-ut-ink">
      <header className="sticky top-0 z-40 flex items-center justify-between gap-3 border-b border-ut-line bg-ut-bg2 px-4 py-3">
        <Link to="/admin" className="flex items-center gap-2">
          <img src={UT_LOGO} alt="" className="h-[34px]" />
          <span className="flex flex-col leading-none">
            <span className="font-ut-display text-lg font-extrabold tracking-[0.04em]">WNF ADMIN</span>
            <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-ut-mut">
              {role}
              {viewer.friendlyName ? ` · ${viewer.friendlyName}` : ''}
            </span>
          </span>
        </Link>
        <Link
          to="/games"
          className="rounded-lg border border-ut-line px-2.5 py-[7px] font-ut-display text-[13px] font-bold tracking-[0.1em] text-ut-mut hover:text-ut-ink"
        >
          EXIT ADMIN
        </Link>
      </header>

      <div ref={chipRow} className="ut-scroll-x flex gap-1.5 overflow-x-auto border-b border-ut-line bg-ut-bg2 px-3 py-2.5 ut-md:hidden">
        <Chip to="/admin" active={pathname === '/admin'} label="Home" />
        {flat.map((i) => (
          <Chip key={i.to} to={i.to} active={isActive(i, pathname)} label={i.label} />
        ))}
      </div>

      <div className="flex flex-1 items-stretch">
        <nav aria-label="Admin" className="hidden w-[210px] flex-none flex-col gap-[18px] border-r border-ut-line px-3 py-[18px] ut-md:flex">
          <Link
            to="/admin"
            aria-current={pathname === '/admin' ? 'page' : undefined}
            className={clsx(
              'rounded-lg px-2.5 py-2 text-sm font-semibold',
              pathname === '/admin' ? 'bg-ut-acc-soft text-ut-acc' : 'text-ut-mut hover:text-ut-ink',
            )}
          >
            Home
          </Link>
          {nav.map((g) => (
            <div key={g.group} className="flex flex-col gap-0.5">
              <span className="px-2.5 pb-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-ut-mut">{g.group}</span>
              {g.items.map((i) => {
                const active = isActive(i, pathname);
                return (
                  <Link
                    key={i.to}
                    to={i.to}
                    aria-current={active ? 'page' : undefined}
                    className={clsx('rounded-lg px-2.5 py-2 text-sm font-semibold', active ? 'bg-ut-acc-soft text-ut-acc' : 'text-ut-mut hover:text-ut-ink')}
                  >
                    {i.label}
                  </Link>
                );
              })}
            </div>
          ))}
        </nav>
        <main className="ut-classic flex min-w-0 flex-1 flex-col gap-[18px] px-3.5 pb-10 pt-4 ut-md:px-[22px] ut-md:pt-[22px] lg:px-[30px] lg:pt-[26px]">
          <Suspense fallback={<UtLoading />}>{children}</Suspense>
        </main>
      </div>
    </div>
  );
};

const Chip: React.FC<{ to: string; active: boolean; label: string }> = ({ to, active, label }) => (
  <Link
    to={to}
    aria-current={active ? 'page' : undefined}
    className={clsx(
      'flex-none whitespace-nowrap rounded-full border px-3 py-[7px] font-ut-display text-sm font-bold tracking-[0.04em]',
      active ? 'border-ut-acc bg-ut-acc-soft text-ut-acc' : 'border-ut-line text-ut-mut',
    )}
  >
    {label}
  </Link>
);
